import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { usePublicAdmin } from "@/contexts/PublicAdminAuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Download, Plus, X, GripVertical, ChevronDown, ChevronUp, Star, Target, AlertTriangle, FileText, Edit2 } from "lucide-react";

const CHANCE_COLORS = {
  "Safe Zone":     { bg: "rgba(56,189,248,0.15)", border: "rgba(56,189,248,0.4)", text: "#38bdf8" },
  "High Chance":   { bg: "rgba(16,185,129,0.15)", border: "rgba(16,185,129,0.4)", text: "#10b981" },
  "Good Chance":   { bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.35)", text: "#3b82f6" },
  "Worth Trying":  { bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.35)", text: "#a78bfa" },
  "Risky Choice":  { bg: "rgba(234,179,8,0.12)", border: "rgba(234,179,8,0.35)", text: "#eab308" },
  "Possible (High Risk)": { bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.30)", text: "#ef4444" },
  "Slight Risk":   { bg: "rgba(249,115,22,0.12)", border: "rgba(249,115,22,0.35)", text: "#f97316" },
  "Low Chance":    { bg: "rgba(249,115,22,0.12)", border: "rgba(249,115,22,0.35)", text: "#f97316" },
  "Dream College": { bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.30)", text: "#ef4444" },
};

export const FOUR_TIER_COLORS = {
  "Dream":    { bg: "rgba(244,63,94,0.15)",  border: "rgba(244,63,94,0.4)",  text: "#f43f5e" },
  "Target":   { bg: "rgba(245,158,11,0.15)", border: "rgba(245,158,11,0.4)", text: "#f59e0b" },
  "Possible": { bg: "rgba(59,130,246,0.15)",  border: "rgba(59,130,246,0.4)",  text: "#3b82f6" },
  "Safe":     { bg: "rgba(16,185,129,0.15)", border: "rgba(16,185,129,0.4)", text: "#10b981" },
};

export const TIERS_ORDER = ["Dream", "Target", "Possible", "Safe"];

export function getFourTierClassification(item) {
  if (item.custom_tier) return item.custom_tier;

  let margin = item.margin_percent;
  if (margin === undefined || margin === null) {
    if (item.cutoff_rank && item.student_rank) {
      margin = ((item.cutoff_rank - item.student_rank) / item.cutoff_rank) * 100;
    }
  }

  if (margin !== undefined && margin !== null) {
    if (margin < -8) return "Dream";
    if (margin <= 8) return "Target";
    if (margin <= 25) return "Possible";
    return "Safe";
  }

  const label = item.chance_label;
  if (label === "Safe Zone") return "Safe";
  if (label === "High Chance" || label === "Good Chance") return "Possible";
  if (label === "Worth Trying" || label === "Risky Choice" || label === "Slight Risk" || label === "Possible (High Risk)") return "Target";
  return "Dream";
}

const STREAM_LABELS = {
  STATE_QUOTA: "State Quota", AIIMS: "AIIMS", AIQ_15: "AIQ 15%",
  DEEMED: "Deemed", OTHER_STATES: "Other States",
};

const TAG_OPTIONS = [
  { value: "primary", label: "🥇 Primary", color: "#38bdf8" },
  { value: "betterment", label: "🥈 Betterment", color: "#10b981" },
  { value: "backup", label: "🥉 Backup", color: "#a78bfa" },
  { value: "stray", label: "⚠️ Stray Vacancy", color: "#ef4444" },
];

const CHANCE_ORDER = [
  "Safe Zone", "High Chance", "Good Chance", "Worth Trying",
  "Risky Choice", "Possible (High Risk)", "Slight Risk", "Low Chance", "Dream College"
];

function CollegeCard({ p, onAdd, alreadyAdded }) {
  const [activeMatchKey, setActiveMatchKey] = useState(null);

  const displayInfo = activeMatchKey && p.all_matches && p.all_matches[activeMatchKey] 
    ? p.all_matches[activeMatchKey] 
    : p;

  const cc = CHANCE_COLORS[displayInfo.chance_label] || CHANCE_COLORS["Dream College"];
  const isStray = p.expected_rounds && p.expected_rounds.some(r => r.round === "R4");
  
  const buffer = (displayInfo.student_rank && displayInfo.cutoff_rank) ? Math.round(displayInfo.student_rank - displayInfo.cutoff_rank) : 0;
  const bufferColor = buffer <= 0 ? "#10b981" : "#ef4444";
  const bufferSign = buffer <= 0 ? "-" : "+";

  // Dynamically compute better opportunity relative to currently displayed match
  let currentAltOpportunity = null;
  if (p.all_matches) {
    const currentMargin = displayInfo.margin_percent !== undefined ? displayInfo.margin_percent : -999;
    const currentLabel = displayInfo.chance_label;
    for (const [rt, match] of Object.entries(p.all_matches)) {
      if (rt === (displayInfo.matched_rank_type || p.matched_rank_type)) continue;
      if (match.margin_percent > currentMargin + 0.01 && match.chance_label !== currentLabel) {
        if (!currentAltOpportunity || match.margin_percent > currentAltOpportunity.margin_percent) {
          currentAltOpportunity = match;
        }
      }
    }
  } else {
    currentAltOpportunity = p.alt_opportunity;
  }

  const handleToggleMatch = () => {
    if (!p.all_matches) return;
    const keys = Object.keys(p.all_matches);
    if (keys.length <= 1) return;
    
    const currentKey = activeMatchKey || p.matched_rank_type;
    const currentIndex = keys.indexOf(currentKey);
    const nextIndex = (currentIndex + 1) % keys.length;
    setActiveMatchKey(keys[nextIndex]);
  };

  return (
    <div style={{
      padding: "10px 12px", background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, marginBottom: 6,
      opacity: alreadyAdded ? 0.5 : 1, borderLeft: `3px solid ${cc.text}`
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#e2e8f0", lineHeight: 1.3, wordBreak: "break-word" }}>{p.college_name}</div>
          <div style={{ fontSize: "0.58rem", color: "#64748b", marginTop: 2 }}>
            {[p.college_code, p.status, p.state].filter(Boolean).join(" · ")}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
            <span style={{ fontSize: "0.55rem", padding: "1px 5px", borderRadius: 3, background: cc.bg, border: `1px solid ${cc.border}`, color: cc.text, fontWeight: 700 }}>{displayInfo.chance_label}</span>
            {p.expected_rounds && p.expected_rounds.map((er, i) => (
              <span key={i} style={{ fontSize: "0.55rem", padding: "1px 5px", borderRadius: 3, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: isStray && er.round === "R4" ? "#ef4444" : "#94a3b8", fontWeight: 600 }}>
                {er.round}{isStray && er.round === "R4" ? " ⚠️" : ""}
              </span>
            ))}
            {p.fees && <span style={{ fontSize: "0.55rem", color: "#10b981" }}>{p.fees}</span>}
          </div>
        </div>

        {/* Calculation Data Block */}
        <div 
          onClick={p.all_matches && Object.keys(p.all_matches).length > 1 ? handleToggleMatch : undefined}
          style={{ 
            display: "flex", flexDirection: "column", alignItems: "flex-end", marginRight: 2, 
            borderRight: "1px solid rgba(255,255,255,0.06)", paddingRight: 10,
            cursor: p.all_matches && Object.keys(p.all_matches).length > 1 ? "pointer" : "default",
            userSelect: "none"
          }}
          title={p.all_matches && Object.keys(p.all_matches).length > 1 ? "Click to toggle calculation basis" : ""}
        >
          <span style={{ fontSize: "0.5rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700, marginBottom: 2 }}>
            {displayInfo.matched_rank_type || "RANK"} MATCH {p.all_matches && Object.keys(p.all_matches).length > 1 && <span style={{ color: "#38bdf8", marginLeft: 2 }}>⟳</span>}
          </span>
          <div style={{ fontSize: "0.6rem", color: "#94a3b8", display: "flex", gap: 4, fontWeight: 600 }}>
            <span title="Your Rank">{displayInfo.student_rank ? displayInfo.student_rank.toLocaleString() : "—"}</span>
            <span style={{ color: "#475569" }}>/</span>
            <span title="Cutoff Rank" style={{ color: "#cbd5e1" }}>{displayInfo.cutoff_rank ? displayInfo.cutoff_rank.toLocaleString() : "—"}</span>
          </div>
          {(displayInfo.student_rank && displayInfo.cutoff_rank) ? (
            <div style={{ fontSize: "0.6rem", fontWeight: 700, color: bufferColor, marginTop: 2 }}>
              {bufferSign}{Math.abs(buffer).toLocaleString()}
            </div>
          ) : null}
        </div>

        <button
          onClick={() => !alreadyAdded && onAdd(p)}
          disabled={alreadyAdded}
          style={{
            padding: "4px 8px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 700,
            border: alreadyAdded ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(56,189,248,0.3)",
            background: alreadyAdded ? "rgba(255,255,255,0.03)" : "rgba(56,189,248,0.1)",
            color: alreadyAdded ? "#334155" : "#38bdf8", cursor: alreadyAdded ? "default" : "pointer",
            whiteSpace: "nowrap", flexShrink: 0
          }}
        >{alreadyAdded ? "Added" : "+ Add"}</button>
      </div>

      {currentAltOpportunity && (
        <div 
          onClick={handleToggleMatch}
          style={{ 
            marginTop: 8, padding: "6px 8px", background: "rgba(16,185,129,0.08)", 
            border: "1px solid rgba(16,185,129,0.2)", borderRadius: 6, display: "flex", 
            alignItems: "center", gap: 6, cursor: "pointer", transition: "all 0.2s" 
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(16,185,129,0.15)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(16,185,129,0.08)"}
          title="Click to toggle calculation basis"
        >
          <span style={{ fontSize: "0.65rem" }}>💡</span>
          <div style={{ fontSize: "0.6rem", color: "#34d399", lineHeight: 1.3 }}>
            <strong>Better Opportunity Detected:</strong> This student has a <strong>{currentAltOpportunity.chance_label}</strong> chance via <strong>{currentAltOpportunity.matched_rank_type}</strong> (Your Rank: {currentAltOpportunity.student_rank?.toLocaleString()}, Cutoff: {currentAltOpportunity.cutoff_rank?.toLocaleString()}). <span style={{ color: "#6ee7b7", marginLeft: 4 }}>Click to view ⟳</span>
          </div>
        </div>
      )}
    </div>
  );
}

function SuggestionRow({ item, index, onRemove, onTagChange, onNoteChange, onTierChange, onMoveUp, onMoveDown, isFirst, isLast }) {
  const tier = getFourTierClassification(item);
  const cc = FOUR_TIER_COLORS[tier] || FOUR_TIER_COLORS["Dream"];
  const tag = TAG_OPTIONS.find(t => t.value === item.tag);
  const isStray = item.expected_rounds && item.expected_rounds.some(r => r.round === "R4");
  const buffer = (item.student_rank && item.cutoff_rank) ? Math.round(item.student_rank - item.cutoff_rank) : 0;
  const bufferColor = buffer <= 0 ? "#10b981" : "#ef4444";
  const bufferSign = buffer <= 0 ? "-" : "+";

  const handleCycleTier = (e) => {
    e.stopPropagation();
    const currentIdx = TIERS_ORDER.indexOf(tier);
    const nextIdx = (currentIdx + 1) % TIERS_ORDER.length;
    onTierChange(index, TIERS_ORDER[nextIdx]);
  };

  return (
    <div style={{
      display: "flex", gap: 8, padding: "10px 12px", background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, marginBottom: 6,
      borderLeft: `3px solid ${cc.text}`, alignItems: "flex-start"
    }}>
      {/* Serial + Move */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 28 }}>
        <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#38bdf8" }}>#{index + 1}</span>
        <button onClick={onMoveUp} disabled={isFirst} style={{ background: "none", border: "none", color: isFirst ? "#334155" : "#64748b", cursor: isFirst ? "default" : "pointer", padding: "1px" }}><ChevronUp size={12} /></button>
        <button onClick={onMoveDown} disabled={isLast} style={{ background: "none", border: "none", color: isLast ? "#334155" : "#64748b", cursor: isLast ? "default" : "pointer", padding: "1px" }}><ChevronDown size={12} /></button>
      </div>
      {/* College info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#e2e8f0", lineHeight: 1.3, wordBreak: "break-word" }}>{item.college_name}</div>
        <div style={{ fontSize: "0.58rem", color: "#64748b", marginTop: 1 }}>{[item.college_code, item.state, item.stream ? STREAM_LABELS[item.stream] : null].filter(Boolean).join(" · ")}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
          <span 
            onClick={handleCycleTier}
            title="Click to cycle tier (Dream → Target → Possible → Safe)"
            style={{ 
              fontSize: "0.55rem", padding: "1px 6px", borderRadius: 3, 
              background: cc.bg, border: `1px solid ${cc.border}`, color: cc.text, 
              fontWeight: 700, cursor: "pointer", userSelect: "none", transition: "all 0.15s",
              display: "inline-flex", alignItems: "center", gap: 3
            }}
            onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.25)"}
            onMouseLeave={e => e.currentTarget.style.filter = "none"}
          >
            {tier} <span style={{ fontSize: "0.5rem", opacity: 0.7 }}>⟳</span>
          </span>
          {item.expected_rounds && item.expected_rounds.map((er, i) => (
            <span key={i} style={{ fontSize: "0.55rem", padding: "1px 5px", borderRadius: 3, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: isStray && er.round === "R4" ? "#ef4444" : "#94a3b8" }}>
              {er.round}{isStray && er.round === "R4" ? " ⚠️" : ""}
            </span>
          ))}
          {tag && <span style={{ fontSize: "0.55rem", padding: "1px 5px", borderRadius: 3, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: tag.color }}>{tag.label}</span>}
          {item.fees && <span style={{ fontSize: "0.55rem", color: "#10b981" }}>{item.fees}</span>}
          {isStray && <span style={{ fontSize: "0.55rem", color: "#ef4444" }}>⚠️ Check Seat Matrix</span>}
        </div>
        {/* Tag selector + Notes */}
        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <select value={item.tag || ""} onChange={e => onTagChange(index, e.target.value)} style={{ fontSize: "0.62rem", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#94a3b8", borderRadius: 4, padding: "2px 4px", fontFamily: "inherit", outline: "none" }}>
            <option value="">Tag…</option>
            {TAG_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input value={item.note || ""} onChange={e => onNoteChange(index, e.target.value)} placeholder="Counsellor note…" style={{ flex: 1, fontSize: "0.62rem", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#cbd5e1", borderRadius: 4, padding: "2px 6px", fontFamily: "inherit", outline: "none", minWidth: 80 }} />
        </div>
      </div>
      
      {/* Calculation Data Block */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", marginRight: 2, borderRight: "1px solid rgba(255,255,255,0.06)", paddingRight: 10 }}>
        <span style={{ fontSize: "0.5rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700, marginBottom: 2 }}>
          {item.matched_rank_type || "RANK"} MATCH
        </span>
        <div style={{ fontSize: "0.6rem", color: "#94a3b8", display: "flex", gap: 4, fontWeight: 600 }}>
          <span title="Your Rank">{item.student_rank ? item.student_rank.toLocaleString() : "—"}</span>
          <span style={{ color: "#475569" }}>/</span>
          <span title="Cutoff Rank" style={{ color: "#cbd5e1" }}>{item.cutoff_rank ? item.cutoff_rank.toLocaleString() : "—"}</span>
        </div>
        {(item.student_rank && item.cutoff_rank) ? (
          <div style={{ fontSize: "0.6rem", fontWeight: 700, color: bufferColor, marginTop: 2 }}>
            {bufferSign}{Math.abs(buffer).toLocaleString()}
          </div>
        ) : null}
      </div>

      <button onClick={() => onRemove(index)} style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#f87171", padding: "4px 6px", borderRadius: 6, cursor: "pointer", lineHeight: 0, flexShrink: 0 }}><X size={12} /></button>
    </div>
  );
}

export default function MedicalCounsellorWorkspace() {
  const { studentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = usePublicAdmin();
  const authHdr = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestionList, setSuggestionList] = useState([]);
  const [counsellorNotes, setCounsellorNotes] = useState("");
  const [streamFilter, setStreamFilter] = useState("ALL");
  const [chanceFilter, setChanceFilter] = useState("ALL");
  const [collegeSearch, setCollegeSearch] = useState("");

  // Prediction data: from navigation state (freshly created) or from DB
  const [predictionData, setPredictionData] = useState(location.state?.prediction_data || []);
  const [predictionMode, setPredictionMode] = useState("BOTH");
  const [includeAir, setIncludeAir] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/public-admin/medical-students/${studentId}`, authHdr());
        setStudent(res.data);
        setSuggestionList(res.data.suggestion_list || []);
        setCounsellorNotes(res.data.counsellor_notes || "");
        if (res.data.prediction_data && res.data.prediction_data.length > 0) {
          let pdata = res.data.prediction_data;
          if (!pdata[0]._course) {
            const firstCourse = res.data.course ? res.data.course.split(",")[0].trim() : "MBBS";
            pdata = pdata.map(p => ({ ...p, _course: firstCourse }));
          }
          setPredictionData(pdata);
        }
        if (res.data.prediction_mode) {
          setPredictionMode(res.data.prediction_mode);
        }
        if (res.data.prediction_include_air !== undefined) {
          setIncludeAir(res.data.prediction_include_air);
        }
        if (res.data.course) {
          setSelectedCourses(res.data.course.split(",").map(c => c.trim()));
        } else {
          setSelectedCourses(["MBBS"]);
        }
      } catch { toast.error("Failed to load student"); navigate("/portal-admin/medical-counsellor"); }
      finally { setLoading(false); }
    };
    load();
  }, [studentId]); // eslint-disable-line

  const handleFetchMoreCourses = async (overrideStudent = null) => {
    const currentStudent = overrideStudent || student;
    if (!currentStudent) return;
    setIsFetchingMore(true);
    try {
      let newPredictions = [];
      for (const course of selectedCourses) {
        const payload = {
          stream: "STATE_QUOTA",
          course: course,
          state: currentStudent.state || "",
          category: currentStudent.category || "",
          gender: currentStudent.gender || "",
          air: currentStudent.air || null,
          sml: currentStudent.sml || null,
          category_rank: currentStudent.category_rank || null,
          target_round: "ALL",
          prediction_mode: predictionMode,
          include_air: includeAir
        };
        try {
          console.log("Fetching for course", course, "Payload:", payload);
          const res = await api.post("/medical-predictor/predict", payload);
          console.log("Result:", res.data);
          const results = (res.data.predictions || []).map(p => ({ ...p, _course: course }));
          newPredictions = [...newPredictions, ...results];
        } catch (e) {
          console.error(`Failed to fetch for ${course}`, e);
        }
      }
      
      console.log("Total new predictions:", newPredictions.length);
      const newCourseStr = selectedCourses.join(", ");
      await api.put(`/public-admin/medical-students/${studentId}`, { 
        course: newCourseStr,
        prediction_data: newPredictions,
        prediction_mode: predictionMode,
        prediction_include_air: includeAir
      }, authHdr());
      
      setPredictionData(newPredictions);
      setStudent(prev => ({ ...prev, course: newCourseStr, prediction_data: newPredictions }));
      toast.success("College pool updated!");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update college pool");
    } finally {
      setIsFetchingMore(false);
    }
  };

  const handleOpenEdit = () => {
    setEditFormData({
      air: student.air || "",
      sml: student.sml || "",
      category_rank: student.category_rank || "",
      category: student.category || "",
      gender: student.gender || "",
      state: student.state || "MAHARASHTRA"
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    setIsSavingEdit(true);
    try {
      const payload = {
        air: editFormData.air ? parseInt(editFormData.air, 10) : null,
        sml: editFormData.sml ? parseInt(editFormData.sml, 10) : null,
        category_rank: editFormData.category_rank ? parseInt(editFormData.category_rank, 10) : null,
        category: editFormData.category,
        gender: editFormData.gender,
        state: "MAHARASHTRA" // Strictly locked for 85% State Quota
      };
      await api.put(`/public-admin/medical-students/${studentId}`, payload, authHdr());
      
      const updatedStudent = { ...student, ...payload };
      setStudent(updatedStudent);
      setIsEditModalOpen(false);
      toast.success("Profile updated! Recalculating chances...");
      
      await handleFetchMoreCourses(updatedStudent);
    } catch (e) {
      toast.error("Failed to update profile");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const addToList = (p) => {
    const already = suggestionList.some(s => s.college_name === p.college_name && s.matched_category === p.matched_category);
    if (already) { toast.info("Already added"); return; }
    setSuggestionList(prev => [...prev, { ...p, tag: "", note: "" }]);
  };

  const removeFromList = (idx) => setSuggestionList(prev => prev.filter((_, i) => i !== idx));

  const handlePullIdealList = () => {
    if (!predictionData || predictionData.length === 0) {
      toast.error("No prediction data available to pull from.");
      return;
    }
    const idealColleges = predictionData.filter(p => p.priority_order !== undefined && p.priority_order !== null && p.priority_order < 999);
    if (idealColleges.length === 0) {
      toast.error("No colleges matched the Ideal Priority List.");
      return;
    }
    
    // Sort by priority order
    idealColleges.sort((a, b) => a.priority_order - b.priority_order);

    const newSuggestions = [];
    idealColleges.forEach(p => {
      const alreadyAdded = suggestionList.some(s => s.college_name === p.college_name && s.matched_category === p.matched_category);
      if (!alreadyAdded) {
        newSuggestions.push({ ...p, note: "", tag: "", custom_tier: null });
      }
    });

    if (newSuggestions.length === 0) {
      toast.info("All ideal colleges are already in the list.");
      return;
    }

    setSuggestionList(prev => [...prev, ...newSuggestions]);
    toast.success(`Pulled ${newSuggestions.length} colleges based on the Ideal Priority List!`);
  };

  const moveItem = (idx, dir) => {
    setSuggestionList(prev => {
      const arr = [...prev];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  const updateTag = (idx, tag) => setSuggestionList(prev => prev.map((item, i) => i === idx ? { ...item, tag } : item));
  const updateNote = (idx, note) => setSuggestionList(prev => prev.map((item, i) => i === idx ? { ...item, note } : item));
  const updateTier = (idx, custom_tier) => setSuggestionList(prev => prev.map((item, i) => i === idx ? { ...item, custom_tier } : item));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/public-admin/medical-students/${studentId}`, { suggestion_list: suggestionList, counsellor_notes: counsellorNotes }, authHdr());
      toast.success("Saved successfully!");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  // Filter prediction data for left panel
  const availableColleges = predictionData.filter(p => {
    const matchStream = streamFilter === "ALL" || (
      streamFilter === "STATE_QUOTA" ? !p.state || p.state === "Maharashtra" :
      streamFilter === "AIIMS" ? p.matched_rank_type === "AIR" && !p.sml :
      true
    );
    const matchChance = chanceFilter === "ALL" || p.chance_label === chanceFilter;
    const matchSearch = !collegeSearch || p.college_name.toLowerCase().includes(collegeSearch.toLowerCase());
    
    // Completely hide colleges that are already in the priority list
    const alreadyAdded = suggestionList.some(s => s.college_name === p.college_name && s.matched_category === p.matched_category);
    
    return matchStream && matchChance && matchSearch && !alreadyAdded;
  });

  const coursePriority = { MBBS: 1, BDS: 2, BAMS: 3, BHMS: 4, BPTH: 5 };
  availableColleges.sort((a, b) => {
    const aCourse = a._course || "MBBS";
    const bCourse = b._course || "MBBS";
    const pA = coursePriority[aCourse] || 99;
    const pB = coursePriority[bCourse] || 99;
    if (pA !== pB) return pA - pB;
    return (a.cutoff_rank || 0) - (b.cutoff_rank || 0); // ascending cutoff rank
  });

  // 4-Tier Balance analysis
  const safeCount = suggestionList.filter(s => getFourTierClassification(s) === "Safe").length;
  const possibleCount = suggestionList.filter(s => getFourTierClassification(s) === "Possible").length;
  const targetCount = suggestionList.filter(s => getFourTierClassification(s) === "Target").length;
  const dreamCount = suggestionList.filter(s => getFourTierClassification(s) === "Dream").length;
  const strayCount = suggestionList.filter(s => s.expected_rounds && s.expected_rounds.some(r => r.round === "R4")).length;

  const handleDownloadPDF = () => {
    if (!student) return;
    const rowsHtml = suggestionList.map((p, i) => {
      const tier = getFourTierClassification(p);
      const cc = FOUR_TIER_COLORS[tier] || { bg: "#f1f5f9", border: "#cbd5e1", text: "#475569" };
      const tag = TAG_OPTIONS.find(t => t.value === p.tag);
      const expRounds = (p.expected_rounds || []).map(er =>
        `<span style="font-size:9px; padding:1px 4px; border-radius:3px; background:${er.round === "R4" ? "rgba(239,68,68,0.1)" : "#f1f5f9"}; color:${er.round === "R4" ? "#ef4444" : "#475569"}; font-weight:700;">${er.round}${er.round === "R4" ? " ⚠️" : ""}</span>`
      ).join(" ");
      return `<tr style="page-break-inside:avoid;">
        <td style="padding:7px 8px; border-bottom:1px solid #e2e8f0; font-size:11px; font-weight:700; color:#1e293b; text-align:center;">${i + 1}</td>
        <td style="padding:7px 8px; border-bottom:1px solid #e2e8f0;">
          <div style="font-size:11px; font-weight:600; color:#1e293b;">${p.college_name}</div>
          <div style="font-size:9px; color:#64748b;">${[p.college_code, p.state].filter(Boolean).join(" · ")}</div>
        </td>
        <td style="padding:7px 8px; border-bottom:1px solid #e2e8f0; font-size:10px; color:#334155;">${p.matched_category || "—"}</td>
        <td style="padding:7px 8px; border-bottom:1px solid #e2e8f0;">${expRounds || "—"}</td>
        <td style="padding:7px 8px; border-bottom:1px solid #e2e8f0;"><span style="font-size:9.5px; font-weight:700; padding:2px 7px; border-radius:4px; background:${cc.bg}; border:1px solid ${cc.border}; color:${cc.text}; white-space:nowrap;">${tier}</span></td>
        <td style="padding:7px 8px; border-bottom:1px solid #e2e8f0; font-size:10px; color:${tag ? tag.color : "#64748b"};">${tag ? tag.label : "—"}</td>
        <td style="padding:7px 8px; border-bottom:1px solid #e2e8f0; font-size:9.5px; color:#475569;">${p.note || "—"}</td>
      </tr>`;
    }).join("");

    const strayWarning = strayCount > 0 ? `
      <div style="margin-top:20px; background:#fff1f2; border:1px solid #fda4af; border-radius:8px; padding:14px; page-break-inside:avoid;">
        <div style="font-size:13px; font-weight:800; color:#e11d48; margin-bottom:6px;">🔴 Counselling Alert: Stray Vacancy</div>
        <div style="font-size:10.5px; color:#9f1239; line-height:1.5;">
          Some colleges in this list may only have seats available in the Stray Vacancy round. 
          <strong>CRITICAL:</strong> The student is ONLY eligible if they have NOT been allotted or admitted to any seat in previous rounds. 
          There is NO guarantee that seats will remain vacant. Check the official seat matrix and consult the counsellor.
        </div>
      </div>` : "";

    const html = `<!DOCTYPE html><html><head>
      <title>Priority List - ${student.reg_number}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
        body { font-family:'Outfit',sans-serif; padding:28px; color:#1e293b; background:#fff; margin:0; }
        h1 { font-size:22px; font-weight:800; color:#0f172a; margin:0; }
        h1 span { color:#38bdf8; }
        .subtitle { font-size:10px; text-transform:uppercase; letter-spacing:1.5px; color:#64748b; font-weight:700; }
        .header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #38bdf8; padding-bottom:14px; margin-bottom:18px; }
        .badge { background:#e0f2fe; color:#0369a1; padding:3px 10px; border-radius:50px; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:1px; }
        .student-card { background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:14px 18px; margin-bottom:16px; display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
        .info-label { font-size:9px; text-transform:uppercase; letter-spacing:0.5px; color:#94a3b8; font-weight:700; }
        .info-value { font-size:13px; font-weight:700; color:#0f172a; margin-top:2px; }
        table { width:100%; border-collapse:collapse; }
        thead tr { background:#0f172a; }
        th { padding:8px; text-align:left; color:#94a3b8; font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.5px; }
        .balance { display:flex; gap:10px; margin-bottom:14px; flex-wrap:wrap; }
        .balance-item { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:6px 12px; text-align:center; }
        .balance-num { font-size:18px; font-weight:800; }
        .balance-lbl { font-size:9px; color:#64748b; font-weight:600; text-transform:uppercase; }
        .contact-section { margin-top:28px; border-top:2px dashed #e2e8f0; padding-top:14px; }
        .footer { margin-top:30px; border-top:1px solid #e2e8f0; padding-top:12px; display:flex; justify-content:space-between; font-size:9px; color:#94a3b8; }
      </style></head><body>
      <div class="header">
        <div><h1>Admissions <span>Made Easy</span></h1><div class="subtitle">Medical Priority Suggestion List</div></div>
        <div style="text-align:right;"><div class="badge">Counsellor Report</div><div style="font-size:10px; color:#64748b; margin-top:4px;">${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div></div>
      </div>
      <div class="student-card">
        <div><div class="info-label">Reg. Number</div><div class="info-value">${student.reg_number}</div></div>
        ${student.student_name ? `<div><div class="info-label">Student Name</div><div class="info-value">${student.student_name}</div></div>` : ""}
        ${student.air ? `<div><div class="info-label">AIR</div><div class="info-value">${student.air.toLocaleString()}</div></div>` : ""}
        ${student.sml ? `<div><div class="info-label">SML</div><div class="info-value">${student.sml.toLocaleString()}</div></div>` : ""}
        ${student.category_rank ? `<div><div class="info-label">Cat. Rank</div><div class="info-value">${student.category_rank.toLocaleString()}</div></div>` : ""}
        <div><div class="info-label">Category</div><div class="info-value">${student.category || "—"} (${student.gender || "—"})</div></div>
        <div><div class="info-label">Course</div><div class="info-value">${student.course || "—"}</div></div>
        <div><div class="info-label">Stream</div><div class="info-value">${STREAM_LABELS[student.stream] || student.stream || "—"}</div></div>
      </div>
      <div style="margin-bottom:14px;"><h3 style="font-size:11px; text-transform:uppercase; letter-spacing:1px; color:#475569; font-weight:700; margin-bottom:8px;">Priority Balance</h3>
        <div class="balance">
          <div class="balance-item"><div class="balance-num" style="color:#10b981;">${safeCount}</div><div class="balance-lbl">Safe</div></div>
          <div class="balance-item"><div class="balance-num" style="color:#3b82f6;">${possibleCount}</div><div class="balance-lbl">Possible</div></div>
          <div class="balance-item"><div class="balance-num" style="color:#f59e0b;">${targetCount}</div><div class="balance-lbl">Target</div></div>
          <div class="balance-item"><div class="balance-num" style="color:#f43f5e;">${dreamCount}</div><div class="balance-lbl">Dream</div></div>
          ${strayCount > 0 ? `<div class="balance-item"><div class="balance-num" style="color:#f59e0b;">${strayCount}</div><div class="balance-lbl">Stray Vacancy</div></div>` : ''}
        </div>
      </div>
      <table><thead><tr>
        <th style="width:4%">#</th><th style="width:30%">College</th><th style="width:10%">Category</th>
        <th style="width:12%">Exp. Round</th><th style="width:14%">Chance</th><th style="width:10%">Tag</th><th style="width:20%">Counsellor Notes</th>
      </tr></thead><tbody>${rowsHtml}</tbody></table>
      ${strayWarning}
      ${counsellorNotes ? `<div style="margin-top:20px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:12px;"><div style="font-size:10px; text-transform:uppercase; letter-spacing:1px; color:#64748b; font-weight:700; margin-bottom:6px;">Counsellor Notes</div><div style="font-size:11px; color:#334155; line-height:1.6;">${counsellorNotes}</div></div>` : ""}
      <div class="contact-section"><div style="font-size:10px; font-weight:700; color:#0369a1; text-align:center;">This report was prepared by ${student.created_by_name || "AME Counsellor"} using the AME Medical Admissions Predictor Portal</div></div>
      <div class="footer"><span>admissionsmadeeasy.in</span><span>Confidential — For Counselling Use Only</span><span>${new Date().toLocaleString("en-IN")}</span></div>
    </body></html>`;

    const el = document.createElement("div");
    el.innerHTML = html;
    import("html2pdf.js").then(({ default: html2pdf }) => {
      html2pdf().set({ margin: 0, filename: `Priority_List_${student.reg_number}.pdf`, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: "in", format: "a4", orientation: "portrait" } }).from(el).save();
    });
  };

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", color: "#64748b" }}>Loading workspace…</div>;
  if (!student) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "1rem 1.25rem" }}>
      {/* Top Bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate("/portal-admin/medical-counsellor")} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: "0.75rem", fontFamily: "inherit" }}>
            <ArrowLeft size={14} /> All Students
          </button>
          <div style={{ width: 1, height: 16, background: "rgba(255,255,255,0.08)" }} />
          <div>
            <span style={{ fontSize: "1rem", fontWeight: 800, color: "#f8fafc" }}>🏥 {student.reg_number}</span>
            {student.student_name && <span style={{ fontSize: "0.75rem", color: "#64748b", marginLeft: 8 }}>{student.student_name}</span>}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {student.air && <span style={{ fontSize: "0.62rem", padding: "2px 7px", borderRadius: 4, background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", color: "#38bdf8", fontWeight: 700 }}>AIR: {student.air.toLocaleString()}</span>}
            {student.sml && <span style={{ fontSize: "0.62rem", padding: "2px 7px", borderRadius: 4, background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.25)", color: "#a78bfa", fontWeight: 700 }}>SML: {student.sml.toLocaleString()}</span>}
            {student.category_rank && <span style={{ fontSize: "0.62rem", padding: "2px 7px", borderRadius: 4, background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.25)", color: "#34d399", fontWeight: 700 }}>CAT: {student.category_rank.toLocaleString()}</span>}
            <span style={{ fontSize: "0.62rem", padding: "2px 7px", borderRadius: 4, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#94a3b8" }}>{student.category} · {student.gender} · {student.course}</span>
            <button onClick={handleOpenEdit} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", display: "flex", alignItems: "center", padding: "4px" }} title="Edit Profile">
              <Edit2 size={12} />
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={handleSave} disabled={saving} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)", color: "#10b981", padding: "7px 14px", borderRadius: 7, fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            <Save size={13} /> {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={handleDownloadPDF} disabled={suggestionList.length === 0} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(56,189,248,0.12)", border: "1px solid rgba(56,189,248,0.3)", color: "#38bdf8", padding: "7px 14px", borderRadius: 7, fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            <Download size={13} /> Download PDF
          </button>
        </div>
      </div>

      {/* Smart Balance Bar */}
      {suggestionList.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: "1rem", padding: "10px 14px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: "0.65rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>Balance:</span>
          <span style={{ fontSize: "0.68rem", color: "#10b981", fontWeight: 700 }}>{safeCount} Safe</span>
          <span style={{ fontSize: "0.68rem", color: "#475569" }}>·</span>
          <span style={{ fontSize: "0.68rem", color: "#3b82f6", fontWeight: 700 }}>{possibleCount} Possible</span>
          <span style={{ fontSize: "0.68rem", color: "#475569" }}>·</span>
          <span style={{ fontSize: "0.68rem", color: "#f59e0b", fontWeight: 700 }}>{targetCount} Target</span>
          <span style={{ fontSize: "0.68rem", color: "#475569" }}>·</span>
          <span style={{ fontSize: "0.68rem", color: "#f43f5e", fontWeight: 700 }}>{dreamCount} Dream</span>
          {strayCount > 0 && <><span style={{ fontSize: "0.68rem", color: "#475569" }}>·</span><span style={{ fontSize: "0.68rem", color: "#f59e0b", fontWeight: 700 }}>⚠️ {strayCount} Stray Vacancy</span></>}
          {safeCount === 0 && <span style={{ fontSize: "0.65rem", color: "#f87171", fontWeight: 600, marginLeft: "auto" }}>⚠️ No safe college in list — add at least one safe option!</span>}
        </div>
      )}

      {/* Two-panel layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", flex: 1, minHeight: 0 }}>
        {/* LEFT — College Pool */}
        <div style={{ display: "flex", flexDirection: "column", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#f8fafc", marginBottom: 8 }}>📋 College Pool <span style={{ color: "#64748b", fontWeight: 400 }}>({predictionData.length} colleges)</span></div>
            
            {/* Multi-Course Fetching UI */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12, alignItems: "center", background: "rgba(0,0,0,0.2)", padding: "8px 10px", borderRadius: 6 }}>
              <span style={{ fontSize: "0.65rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Fetch Courses:</span>
              {['MBBS', 'BDS', 'BAMS', 'BHMS', 'BPTH'].map(c => (
                <label key={c} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.68rem", color: "#cbd5e1", cursor: "pointer", fontWeight: 600 }}>
                  <input type="checkbox" checked={selectedCourses.includes(c)} onChange={(e) => {
                    if (e.target.checked) setSelectedCourses(prev => [...prev, c]);
                    else setSelectedCourses(prev => prev.filter(x => x !== c));
                  }} style={{ cursor: "pointer", accentColor: "#10b981" }} /> {c}
                </label>
              ))}
              <button 
                onClick={() => handleFetchMoreCourses()} 
                disabled={isFetchingMore}
                style={{ marginLeft: "auto", padding: "4px 10px", background: "linear-gradient(135deg, #10b981, #059669)", border: "none", color: "#fff", borderRadius: 4, fontSize: "0.65rem", fontWeight: 700, cursor: isFetchingMore ? "default" : "pointer", opacity: isFetchingMore ? 0.7 : 1, transition: "all 0.2s" }}
              >
                {isFetchingMore ? "Fetching..." : "Fetch Data"}
              </button>
            </div>
            
            <div style={{ display: "flex", gap: 10, alignItems: "center", background: "rgba(0,0,0,0.2)", padding: "8px 10px", borderRadius: 6, flexWrap: "wrap", marginBottom: 12 }}>
              <span style={{ fontSize: "0.65rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Prediction Basis:</span>
              <select value={predictionMode} onChange={e => { setPredictionMode(e.target.value); }} style={{ padding: "4px 6px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, color: "#cbd5e1", fontSize: "0.65rem", fontFamily: "inherit", outline: "none", cursor: "pointer" }}>
                <option value="BOTH" style={{ background: "#0f172a", color: "#f8fafc" }}>Best Match (Both)</option>
                <option value="SML" style={{ background: "#0f172a", color: "#f8fafc" }}>Strictly SML</option>
                <option value="CAT_RANK" style={{ background: "#0f172a", color: "#f8fafc" }}>Strictly Cat. Rank</option>
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.68rem", color: "#cbd5e1", cursor: "pointer", fontWeight: 600, marginLeft: 8 }}>
                <input type="checkbox" checked={includeAir} onChange={(e) => setIncludeAir(e.target.checked)} style={{ cursor: "pointer", accentColor: "#10b981" }} /> 
                Include AIR
              </label>
              {predictionMode !== "BOTH" && <span style={{ fontSize: "0.6rem", color: "#fbbf24" }}>⚠️ Strict mode overrides best chance</span>}
            </div>

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <input value={collegeSearch} onChange={e => setCollegeSearch(e.target.value)} placeholder="Search…" style={{ flex: 1, minWidth: 100, padding: "4px 8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, color: "#f8fafc", fontSize: "0.68rem", outline: "none", fontFamily: "inherit" }} />
              <select value={chanceFilter} onChange={e => setChanceFilter(e.target.value)} style={{ padding: "4px 6px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, color: "#94a3b8", fontSize: "0.65rem", fontFamily: "inherit", outline: "none" }}>
                <option value="ALL">All Chances</option>
                {CHANCE_ORDER.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}>
            {predictionData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "2rem", color: "#475569", fontSize: "0.75rem" }}>No prediction data. Go back to predictor and run a prediction first.</div>
            ) : availableColleges.length === 0 ? (
              <div style={{ textAlign: "center", padding: "2rem", color: "#475569", fontSize: "0.75rem" }}>No colleges match your filter.</div>
            ) : availableColleges.map((p, i) => {
              const alreadyAdded = suggestionList.some(s => s.college_name === p.college_name && s.matched_category === p.matched_category);
              return <CollegeCard key={i} p={p} onAdd={addToList} alreadyAdded={alreadyAdded} />;
            })}
          </div>
        </div>

        {/* RIGHT — Priority List */}
        <div style={{ display: "flex", flexDirection: "column", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#f8fafc" }}>✅ Priority List <span style={{ color: "#64748b", fontWeight: 400 }}>({suggestionList.length} items)</span></div>
              {suggestionList.length > 0 && <span style={{ fontSize: "0.6rem", color: "#475569", display: "block", marginTop: 2 }}>Drag #↑↓ to reorder</span>}
            </div>
            <button 
              onClick={handlePullIdealList}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg, #10b981, #059669)", border: "none", color: "#fff", padding: "6px 12px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
              title="Add all available colleges according to the master ideal priority sequence"
            >
              ⚡ PULL - IDEAL CHOICE FILLING LIST
            </button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}>
            {suggestionList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "#475569" }}>
                <Plus size={30} style={{ opacity: 0.3, marginBottom: 10 }} />
                <div style={{ fontSize: "0.8rem", fontWeight: 600 }}>List is empty</div>
                <div style={{ fontSize: "0.7rem", marginTop: 6 }}>Click <strong style={{ color: "#38bdf8" }}>+ Add</strong> on colleges from the left panel</div>
              </div>
            ) : suggestionList.map((item, idx) => (
              <SuggestionRow
                key={idx} item={item} index={idx}
                onRemove={removeFromList}
                onTagChange={updateTag}
                onNoteChange={updateNote}
                onTierChange={updateTier}
                onMoveUp={() => moveItem(idx, -1)}
                onMoveDown={() => moveItem(idx, 1)}
                isFirst={idx === 0} isLast={idx === suggestionList.length - 1}
              />
            ))}
          </div>
          {/* Counsellor Notes */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "10px 12px" }}>
            <div style={{ fontSize: "0.62rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>Counsellor Notes</div>
            <textarea
              value={counsellorNotes}
              onChange={e => setCounsellorNotes(e.target.value)}
              placeholder="Add general notes about this student, preferences, budget constraints…"
              style={{ width: "100%", minHeight: 70, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "#cbd5e1", fontSize: "0.7rem", padding: "6px 8px", fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }}
            />
          </div>
        </div>
      </div>

      {isEditModalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 12, padding: "20px", width: "100%", maxWidth: 400 }}>
            <h3 style={{ margin: "0 0 16px 0", color: "#f8fafc", fontSize: "1rem" }}>Edit Student Profile</h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: "0.65rem", color: "#94a3b8", marginBottom: 4 }}>AIR</label>
                <input type="number" value={editFormData.air} onChange={e => setEditFormData({...editFormData, air: e.target.value})} style={{ width: "100%", padding: "6px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff", fontSize: "0.75rem", boxSizing: "border-box", fontFamily: "inherit" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "0.65rem", color: "#94a3b8", marginBottom: 4 }}>SML</label>
                <input type="number" value={editFormData.sml} onChange={e => setEditFormData({...editFormData, sml: e.target.value})} style={{ width: "100%", padding: "6px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff", fontSize: "0.75rem", boxSizing: "border-box", fontFamily: "inherit" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "0.65rem", color: "#94a3b8", marginBottom: 4 }}>Cat. Rank</label>
                <input type="number" value={editFormData.category_rank} onChange={e => setEditFormData({...editFormData, category_rank: e.target.value})} style={{ width: "100%", padding: "6px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff", fontSize: "0.75rem", boxSizing: "border-box", fontFamily: "inherit" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "0.65rem", color: "#94a3b8", marginBottom: 4 }}>Category</label>
                <select value={editFormData.category} onChange={e => setEditFormData({...editFormData, category: e.target.value})} style={{ width: "100%", padding: "6px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff", fontSize: "0.75rem", boxSizing: "border-box", fontFamily: "inherit" }}>
                  {["OPEN", "OBC", "SC", "ST", "EWS", "VJA", "NTB", "NTC", "NTD", "SEBC"].map(c => <option key={c} value={c} style={{ background: "#0f172a", color: "#f8fafc" }}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "0.65rem", color: "#94a3b8", marginBottom: 4 }}>Gender</label>
                <select value={editFormData.gender} onChange={e => setEditFormData({...editFormData, gender: e.target.value})} style={{ width: "100%", padding: "6px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff", fontSize: "0.75rem", boxSizing: "border-box", fontFamily: "inherit" }}>
                  <option value="Male" style={{ background: "#0f172a", color: "#f8fafc" }}>Male</option>
                  <option value="Female" style={{ background: "#0f172a", color: "#f8fafc" }}>Female</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "0.65rem", color: "#94a3b8", marginBottom: 4 }}>State</label>
                <input type="text" readOnly value="Maharashtra (85% Quota)" style={{ width: "100%", padding: "6px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 6, color: "#64748b", fontSize: "0.75rem", boxSizing: "border-box", fontFamily: "inherit", cursor: "not-allowed" }} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setIsEditModalOpen(false)} style={{ flex: 1, padding: "8px", background: "rgba(255,255,255,0.05)", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, fontFamily: "inherit" }}>Cancel</button>
              <button onClick={handleSaveEdit} disabled={isSavingEdit} style={{ flex: 1, padding: "8px", background: "#38bdf8", border: "none", borderRadius: 6, color: "#0f172a", cursor: "pointer", fontSize: "0.75rem", fontWeight: 700, fontFamily: "inherit" }}>
                {isSavingEdit ? "Saving..." : "Save & Recalculate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
